import { Popover, Text } from '@byecode/ui'
import { ActionItemPattern, BACKGROUND_TYPE } from '@lighthouse/core'
import {
    btnTypeList,
    BUTTON_PATTERN_CONTROL,
    FilledInput,
    FillPickerPopoverDropdown,
    getInputInnerTypeMatch,
    getIsFindUseInRichText,
    ListItem4ByecodeUi,
    NodeFieldPreview,
    PURE_ALLOW_ICON_TYPES,
    useAtomData,
    useFindUseObjectContext
} from '@lighthouse/shared'
import React, { useCallback } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import styled from 'styled-components'

import { lastPageOfStackAtom } from '@/atoms/page/state'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useColorSystemAction } from '@/hooks/useColorSystemAction'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useGetColorVariableOptions } from '@/hooks/useGetColorVariableOptions'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'
import { useVariableCustomViewOption } from '@/hooks/useVariableCustomViewOption'

import FormSegmentedControl from '../Common/FormSegmentedControl'
import { IconControl } from '../Common/IconControl'
import * as CM from '../styles'
import { ICON_POSITION_CONTROL } from './constant'
import * as SC from './styles'

const SCxListItemBaseInfo = styled.div`
    gap: 0;
    padding: 0 16px;
`

interface ButtonConfigureProps {
    style?: React.CSSProperties
}

export const ButtonConfigure: React.FunctionComponent<ButtonConfigureProps> = ({ style }) => {
    const { control, watch } = useFormContext()
    const { icon, showType, iconPosition, fillWay, backgroundColor, color, borderColor, pattern } = watch()
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const disabledWithVersion = useIsDisabledWithVersion()

    const findUseObject = useFindUseObjectContext()
    const [stackId, pageId] = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => [s?.stackId || '', s?.pageId || ''], [])
    )

    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })

    const userOption = useGetUserRecordOptions()
    const { customViewOption } = useVariableCustomViewOption()

    const colorSystemMethods = useColorSystemAction()

    const variableTypeOption = useGetColorVariableOptions()

    return (
        <SCxListItemBaseInfo style={style}>
            <ListItem4ByecodeUi
                alignItems="center"
                justifyContent="space-between"
                styles={{
                    root: {
                        gap: 0
                    }
                }}
            >
                <Text color="var(--color-gray-600)">标题</Text>
                <SC.RightFill>
                    <Controller
                        control={control}
                        name="name"
                        render={({ field }) => {
                            const isHighLight = getIsFindUseInRichText({
                                doc: field.value,
                                findUseObject
                            })
                            return (
                                <NodeFieldPreview
                                    innerTypes={getInputInnerTypeMatch('TEXT')}
                                    disabled={disabledWithVersion}
                                    highlighting={isHighLight}
                                    style={{ width: 180 }}
                                    autoHeight
                                    placeholder="输入按钮名称"
                                    viewOptions={customViewOption ? [customViewOption] : undefined}
                                    pageOptions={pageOptions}
                                    userOption={userOption}
                                    value={field.value}
                                    onChange={field.onChange}
                                    onBlur={field.onChange}
                                    dataSourceList={dataSourceList}
                                />
                            )
                        }}
                    />
                </SC.RightFill>
            </ListItem4ByecodeUi>
            <ListItem4ByecodeUi alignItems="center" justifyContent="space-between">
                <Text>按钮元素</Text>
                <SC.RightFill>
                    <Controller
                        name="showType"
                        control={control}
                        defaultValue={showType}
                        render={({ field }) => (
                            <CM.BlockSettingsSelect
                                // withinPortal
                                searchable={false}
                                // rightSection={<IconFont type="ArrowDownSmall" />}
                                name="pointer"
                                defaultValue="icon-name"
                                value={field.value}
                                options={btnTypeList}
                                style={{
                                    width: field.value === 'name' ? 180 : 140
                                }}
                                onChange={field.onChange}
                            />
                        )}
                    />
                    {showType !== 'name' && (
                        <Controller
                            name="icon"
                            control={control}
                            defaultValue={icon}
                            render={({ field }) => (
                                <IconControl
                                    allowIconTypes={PURE_ALLOW_ICON_TYPES}
                                    value={field.value}
                                    onChange={field.onChange}
                                    clearable={false}
                                />
                            )}
                        />
                    )}
                </SC.RightFill>
            </ListItem4ByecodeUi>

            {showType !== 'name' && (
                <Controller
                    name="iconPosition"
                    control={control}
                    defaultValue={iconPosition}
                    render={({ field }) => (
                        <FormSegmentedControl label="图标位置" data={ICON_POSITION_CONTROL} value={field.value} onChange={field.onChange} />
                    )}
                />
            )}
            {/* {!hideConfigs?.fillWay && (
                <Controller
                    name='fillWay'
                    control={control}
                    defaultValue={fillWay}
                    render={({ field }) => (
                        <FormSegmentedControl
                            label="宽度规则"
                            data={FIT_TYPE_CONTROL}
                            value={field.value}
                            compact
                            onChange={field.onChange}
                        />
                    )}
                />
            )} */}
            <Controller
                name="pattern"
                control={control}
                defaultValue={pattern}
                render={({ field }) => (
                    <FormSegmentedControl
                        label="颜色样式"
                        data={BUTTON_PATTERN_CONTROL}
                        value={field.value}
                        size="sm"
                        onChange={field.onChange}
                    />
                )}
            />

            {pattern === ActionItemPattern.custom && (
                <>
                    <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                        <Text>背景色</Text>
                        <Controller
                            name="backgroundColor"
                            control={control}
                            defaultValue={backgroundColor}
                            render={({ field }) => (
                                <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={152}>
                                    <Popover.Target>
                                        <FilledInput
                                            style={{ width: 180 }}
                                            variableTypeOption={variableTypeOption}
                                            value={field.value}
                                            onClear={() => field.onChange({ type: BACKGROUND_TYPE.color })}
                                        />
                                    </Popover.Target>
                                    <FillPickerPopoverDropdown
                                        title="填充"
                                        value={field.value}
                                        onChange={field.onChange}
                                        variableTypeOption={variableTypeOption}
                                        enabledBackgroundTypes={[BACKGROUND_TYPE.color, BACKGROUND_TYPE.gradient, BACKGROUND_TYPE.variable]}
                                        {...colorSystemMethods}
                                    />
                                </Popover>
                            )}
                        />
                    </ListItem4ByecodeUi>
                    <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                        <Text>图标色</Text>
                        <Controller
                            name="iconColor"
                            control={control}
                            render={({ field }) => (
                                <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={152}>
                                    <Popover.Target>
                                        <FilledInput
                                            style={{ width: 180 }}
                                            value={field.value}
                                            onClear={() => field.onChange({ type: BACKGROUND_TYPE.color })}
                                        />
                                    </Popover.Target>
                                    <FillPickerPopoverDropdown
                                        title="填充"
                                        value={field.value}
                                        onChange={field.onChange}
                                        enabledBackgroundTypes={[BACKGROUND_TYPE.color]}
                                        {...colorSystemMethods}
                                    />
                                </Popover>
                            )}
                        />
                    </ListItem4ByecodeUi>
                    <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                        <Text>文字色</Text>
                        <Controller
                            name="color"
                            control={control}
                            defaultValue={color}
                            render={({ field }) => (
                                <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={152}>
                                    <Popover.Target>
                                        <FilledInput
                                            style={{ width: 180 }}
                                            value={field.value}
                                            onClear={() => field.onChange({ type: BACKGROUND_TYPE.color })}
                                        />
                                    </Popover.Target>
                                    <FillPickerPopoverDropdown
                                        title="填充"
                                        value={field.value}
                                        onChange={field.onChange}
                                        enabledBackgroundTypes={[BACKGROUND_TYPE.color, BACKGROUND_TYPE.gradient]}
                                        {...colorSystemMethods}
                                    />
                                </Popover>
                            )}
                        />
                    </ListItem4ByecodeUi>
                    <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                        <Text>描边色</Text>
                        <Controller
                            name="borderColor"
                            control={control}
                            defaultValue={borderColor}
                            render={({ field }) => (
                                <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={152}>
                                    <Popover.Target>
                                        <FilledInput
                                            style={{ width: 180 }}
                                            value={field.value}
                                            onClear={() => field.onChange({ type: BACKGROUND_TYPE.color })}
                                        />
                                    </Popover.Target>
                                    <FillPickerPopoverDropdown
                                        title="填充"
                                        value={field.value}
                                        onChange={field.onChange}
                                        enabledBackgroundTypes={[BACKGROUND_TYPE.color]}
                                        {...colorSystemMethods}
                                    />
                                </Popover>
                            )}
                        />
                    </ListItem4ByecodeUi>
                </>
            )}
        </SCxListItemBaseInfo>
    )
}
